<template>
  <div class="row mt-2 mb-3">
    <div class="col-5 order-setupitem-title-area">
      <h4>{{title}}</h4>
      <div v-if="userHasUpdatePermission(permissionType)" class="order-setupitem-details">
        <div v-for="(approvalRequestReason, index) in approvalRequestReasonList" :key="index">
          <template v-if="isAccountApprovalType">
            <Icon iconStyle="far" icon="fa-credit-card" iconClass="status-credit-danger" class="mr-1" fixedWidth></Icon>
            {{approvalRequestReason}}
          </template>
          <template v-if="isProductionApprovalType">
            <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="order-item-icon-production-error" iconStack="fa-circle" iconStackClass="order-item-icon-production-error-bg" class="mr-1"></Icon>
            <a class="order-setupitem-reason" href="#" @click.prevent="clickedApprovalReason">{{approvalRequestReason}}</a>
          </template>
          <template v-if="isPricingApprovalType">
            <Icon iconStyle="fas" icon="fa-usd-circle" iconClass="order-item-icon-price-error" iconStack="fa-circle" iconStackClass="order-item-icon-price-error-bg" class="mr-1"></Icon>
            <a class="order-setupitem-reason" href="#" @click.prevent="clickedApprovalReason">{{approvalRequestReason}}</a>
          </template>
          <template v-if="isCustomerApprovalType">
            <Icon icon="fa-user-circle" iconClass="order-item-icon-customer-approval" class="mr-1" fixedWidth></Icon>
            <a class="order-setupitem-reason" href="#" @click.prevent="clickedApprovalReason">{{approvalRequestReason}}</a>
          </template>
          <template v-if="isNCRApprovalType">
            <Icon icon="fa-repeat" iconClass="order-item-icon-ncr" class="mr-1" fixedWidth></Icon>
            <a class="order-setupitem-reason" href="#" @click.prevent="clickedApprovalReason">{{approvalRequestReason}}</a>
          </template>
        </div>
      </div>
    </div>
    <div class="col-6">
      <template v-if="getApprovalStatusForType() == ApprovalTypes.pending && isStatusPending">
        <div v-if="userHasUpdatePermission(permissionType) && !isOrderOnHold && approvalEnabled">
          <template v-if="emailEnabled">
            <ProgressTrackerButton @click="$emit('email')" icon="fa-paper-plane">Send Order</ProgressTrackerButton>
            &nbsp;&nbsp;
          </template>
          <ProgressTrackerButton @click="$emit('approve')" icon="fa-thumbs-up">{{approveButtonTitle}}</ProgressTrackerButton>
          &nbsp;&nbsp;
          <ProgressTrackerButton @click="$emit('deny')" icon="fa-thumbs-down">{{denyButtonTitle}}</ProgressTrackerButton>
        </div>
        <div class="mt-1" v-if="(!userHasUpdatePermission(permissionType) || isOrderOnHold) && approvalEnabled">
          <p v-if="isOrderOnHold">Order on hold. Resume order before approval</p>
          <p class="font-weight-bold">Approval required</p>
        </div>        
        <slot name="pending" />
      </template>
      <div v-if="isApprovalTypeApproved()">
        <p class="order-setupitem-result status-approved"><i class="far fa-check-circle"></i> Approved</p>
        <div v-if="approvedNote" class="order-setupitem-notes" v-html="convertNewlinesToBreaks(approvedNote)"></div>
        <FileAttachment v-for="file in attachedFileList" :key="file.id" :file="file" />
        <p>Approved by {{approvalUserName}} at {{approvalDate | formatDateTime}}</p>
        <slot name="approved" />
      </div>
      <div v-if="isApprovalTypeDenied()">
        <p class="order-setupitem-result status-denied"><i class="far fa-times-circle"></i> Denied</p>
        <div v-if="deniedReason" class="order-setupitem-notes" v-html="convertNewlinesToBreaks(deniedReason)"></div>
        <FileAttachment v-for="file in attachedFileList" :key="file.id" :file="file" />
        <p>Denied by {{approvalUserName}} at {{approvalDate | formatDateTime}}</p>
        
        <slot name="denied" />
      </div>
    </div>
    <div class="col-1"> </div>
  </div>
</template>

<script>
import ProgressTrackerButton from '@/components/layout/progressTrackerButton.vue'
import { ApprovalTypes, OrderApprovalTypes, OrderStatusTypes } from '@/store/types'

export default {
  name: 'OrderApprovalBar',
  components: {
    ProgressTrackerButton
  },
  props: {
    title: String,
    approvalType: String,
    approvalEnabled: {
      type: Boolean,
      default: true
    },
    permissionType: String,
    order: Object,
    orderItem: Object,
    approveButtonTitle: {
      type: String,
      default: 'Approve'
    },
    denyButtonTitle: {
      type: String,
      default: 'Deny'
    },
    emailEnabled: {
      type: Boolean,
      default: false
    }
  },
  created() {
    this.ApprovalTypes = ApprovalTypes
    this.OrderApprovalTypes = OrderApprovalTypes
  },
  computed: {
    approvalUserName() {
      let approval = this.getApprovalInfoForType()
      if(approval && approval.user)
        return approval.user.name
      return ''
    },
    approvalDate() {
      let approval = this.getApprovalInfoForType()
      if(approval)
        return approval.approvalUpdatedDate
      return ''
    },
    approvalRequestReasonList() {
      // Just use a stock string for NCR. Clicking will display more details
      if(this.isNCRApprovalType)
        return ['Click for details']

      let approval = this.getApprovalInfoForType()
      if(approval)
        return approval.requestReason.split('\n')
      return []
    },
    approvedNote() {
      let approval = this.getApprovalInfoForType()
      if(approval)
        return approval.approvedNote
      return ''
    },
    deniedReason() {
      let approval = this.getApprovalInfoForType()
      if(approval) {
        var deniedReason = ''

        // Add rejection quantity details if NCR approval
        if(this.isNCRApprovalType) {
          deniedReason += `${approval.rejectedQuantity} item(s) rejected, ${this.orderItem.quantity-approval.rejectedQuantity} item(s) accepted\n`
        }
        deniedReason += approval.deniedReason

        return deniedReason
      }
      return ''
    },
    attachedFileList() {
      let approval = this.getApprovalInfoForType()
      if(approval)
        return approval.userFiles || []
      return []
    },
    isStatusPending() {
      if(this.isNCRApprovalType)
        return this.order.status == OrderStatusTypes.ncrPendingReturn
      return this.order.status == OrderStatusTypes.pendingAccountApproval || 
        this.order.status == OrderStatusTypes.pendingProductionApproval ||
        this.order.status == OrderStatusTypes.pendingCustomerApproval
    },
    isAccountApprovalType() {
      return this.approvalType == OrderApprovalTypes.account
    },
    isProductionApprovalType() {
      return this.approvalType == OrderApprovalTypes.production
    },
    isPricingApprovalType() {
      return this.approvalType == OrderApprovalTypes.pricing
    },
    isCustomerApprovalType() {
      return this.approvalType == OrderApprovalTypes.customer
    },
    isNCRApprovalType() {
      return this.approvalType == OrderApprovalTypes.ncrOrderItem
    },
    isOrderOnHold() {
      return this.order.isOnHold
    }
  },
  methods: {
    getApprovalInfoForType() {
      if(this.isNCRApprovalType) {
        if(!this.orderItem || !this.orderItem.orderApproval)
          return null
        if(this.orderItem.orderApproval.type != this.approvalType)
          return null

        return this.orderItem.orderApproval
      }

      if(!this.order || !this.order.orderApprovals)
        return null
      return this.order.orderApprovals.find(app => app.type == this.approvalType)
    },
    getApprovalStatusForType() {
      let info = this.getApprovalInfoForType()
      if(info == null)
        return ApprovalTypes.pending
      return info.approvalStatus
    },
    isApprovalTypeApproved() {
      return this.getApprovalStatusForType() == ApprovalTypes.approved
    },
    isApprovalTypeDenied() {
      return this.getApprovalStatusForType() == ApprovalTypes.denied
    },
    clickedApprovalReason(event) {
      this.$emit('displayDetails', event)
    }
  }
}
</script>